/**
 * ファイルダウンロード（帳票 xlsx 等）
 *
 * サーバーの `Content-Disposition` からファイル名を取り出して保存する。
 * `filename*=UTF-8''…`（RFC 5987, 日本語ファイル名）を優先し、なければ `filename="…"` を使う。
 *
 * @example
 *   await downloadFile('/reports/monitoring-1', { method: 'POST', body: { year: 2026 } });
 */
import { rawRequest, type QueryParams } from './api';

export function parseContentDispositionFilename(header: string | null | undefined): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (star?.[2]) {
    try {
      return decodeURIComponent(star[2].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fallthrough */
    }
  }
  const quoted = /filename\s*=\s*"((?:\\.|[^"\\])*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1].replace(/\\(.)/g, '$1');
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare?.[1]) {
    const v = bare[1].trim();
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  }
  return null;
}

/** Blob をファイルとして保存させる */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Safari 等でクリック直後に revoke するとダウンロードされないことがあるため遅延
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface DownloadOptions {
  method?: 'GET' | 'POST';
  query?: QueryParams;
  body?: unknown;
  /** Content-Disposition が無い場合のファイル名 */
  fallbackFilename?: string;
  signal?: AbortSignal;
}

/** API を呼び出してレスポンスをファイル保存する。保存したファイル名を返す */
export async function downloadFile(path: string, opts: DownloadOptions = {}): Promise<string> {
  const res = await rawRequest(opts.method ?? 'GET', path, {
    query: opts.query,
    body: opts.body,
    signal: opts.signal,
    headers: { Accept: '*/*' },
  });
  const filename =
    parseContentDispositionFilename(res.headers.get('Content-Disposition')) ?? opts.fallbackFilename ?? 'download';
  const blob = await res.blob();
  saveBlob(blob, filename);
  return filename;
}
